import { jsx, jsxs } from "react/jsx-runtime";
import { P } from "@pcln/horizon";
import { Box, Flex, Relative, Text } from "pcln-design-system";
import "react";
import PricelineLogo from "../../../../svgs/PricelineLogo.js";
import VipLogo from "../../../../svgs/VipLogo.js";
import { parsePrice } from "../../../../utils/conversionUtils.js";
import { PriceCurrency, PriceCurrencyStrikeThrough, TextStrikeThrough } from "../../HotlListingCard.styles.js";
import { getNightlyRate, getSavingsPct, getStrikePrice, getTotalPrice } from "./getPriceUtils.js";
const Price = ({ propertyName, price, totalPrice, currency, savingsPct, priceDisplayRegulation, nightlyRateIncludingTaxesAndFees, nightlyRateExcludingTaxesAndFees, grandTotalExcludingTax, grandTotalSavingsPctExcludingTax, grandTotalStrikeThroughPrice, grandTotalStrikeThroughPriceExcludingTax, nightlyAllRoomsPriceExcludingTax, nights = 1, rooms = 1, isPricingVariant, hasDates })=>{
    const nightlyRate = getNightlyRate({
        priceDisplayRegulation,
        nightlyAllRoomsPriceExcludingTax,
        nightlyRateIncludingTaxesAndFees,
        nightlyRateExcludingTaxesAndFees,
        price
    });
    const resolvedStrikePrice = getStrikePrice({
        priceDisplayRegulation,
        grandTotalStrikeThroughPriceExcludingTax,
        grandTotalStrikeThroughPrice
    });
    const resolvedTotalPrice = getTotalPrice({
        priceDisplayRegulation,
        grandTotalExcludingTax,
        totalPrice
    });
    const resolvedSavingsPct = getSavingsPct({
        priceDisplayRegulation,
        grandTotalSavingsPctExcludingTax,
        savingsPct
    });
    const parsedNightlyRate = parsePrice(nightlyRate);
    const parsedStrikePrice = parsePrice(resolvedStrikePrice);
    const parsedTotalPrice = parsePrice(resolvedTotalPrice);
    if (isPricingVariant) return /*#__PURE__*/ jsxs("div", {
        className: "flex w-full items-center justify-between",
        "aria-label": `Price and Merchandizing information for ${propertyName}`,
        children: [
            /*#__PURE__*/ jsx("section", {
                className: "w-2/3",
                "aria-label": `Merchandizing information for ${propertyName}`,
                children: resolvedSavingsPct ? /*#__PURE__*/ jsxs("div", {
                    className: "flex flex-col",
                    children: [
                        /*#__PURE__*/ jsx("div", {
                            className: "flex flex-row items-start",
                            children: /*#__PURE__*/ jsxs("div", {
                                className: "flex items-center justify-center gap-1 leading-6",
                                children: [
                                    /*#__PURE__*/ jsx("div", {
                                        className: "mt-2 flex h-5",
                                        children: /*#__PURE__*/ jsx(PricelineLogo, {
                                            width: '60px',
                                            height: '24px'
                                        })
                                    }),
                                    /*#__PURE__*/ jsx("div", {
                                        className: "flex",
                                        children: /*#__PURE__*/ jsx(VipLogo, {
                                            width: '24px',
                                            height: '24px'
                                        })
                                    }),
                                    /*#__PURE__*/ jsxs(P, {
                                        className: "text-priceRetail-11 text-heading5 flex leading-6",
                                        children: [
                                            parseInt(resolvedSavingsPct, 10),
                                            "% Off"
                                        ]
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ jsx(P, {
                            className: "text-neutral-10 text-body3 flex",
                            children: "Lower price because you're a VIP!"
                        })
                    ]
                }) : null
            }),
            /*#__PURE__*/ jsxs("section", {
                className: "text-priceRetail-13 flex flex-col",
                "aria-label": `Price information for ${propertyName}`,
                children: [
                    /*#__PURE__*/ jsxs(P, {
                        className: "text-body3 flex items-center justify-end font-light",
                        children: [
                            "Per night ",
                            /*#__PURE__*/ jsx("sup", {
                                className: "ml-1",
                                children: currency
                            }),
                            " ",
                            parsedNightlyRate
                        ]
                    }),
                    /*#__PURE__*/ jsxs("div", {
                        className: "flex items-center justify-end",
                        children: [
                            parsedStrikePrice > 0 ? /*#__PURE__*/ jsx("div", {
                                className: "text-urgency-8 text-body3 relative mr-1 inline-block",
                                children: /*#__PURE__*/ jsxs("span", {
                                    className: "relative",
                                    children: [
                                        /*#__PURE__*/ jsx("sup", {
                                            children: currency
                                        }),
                                        /*#__PURE__*/ jsx("span", {
                                            children: parsedStrikePrice
                                        }),
                                        /*#__PURE__*/ jsx("span", {
                                            className: "pointer-events-none absolute right-0 top-1/2 w-full skew-y-[-13deg] border-b-2 border-[#e11900]",
                                            style: {
                                                content: "''"
                                            },
                                            "aria-hidden": "true"
                                        })
                                    ]
                                })
                            }) : null,
                            hasDates && parsedTotalPrice ? /*#__PURE__*/ jsxs("span", {
                                className: "font-bold leading-5",
                                children: [
                                    /*#__PURE__*/ jsx("sup", {
                                        className: "text-priceSuper-heading4",
                                        children: currency
                                    }),
                                    /*#__PURE__*/ jsx("span", {
                                        className: "text-heading4",
                                        children: parsedTotalPrice
                                    })
                                ]
                            }) : null,
                            hasDates ? null : /*#__PURE__*/ jsxs("span", {
                                className: "font-bold leading-5",
                                children: [
                                    /*#__PURE__*/ jsx("sup", {
                                        className: "text-priceSuper-heading4",
                                        children: currency
                                    }),
                                    /*#__PURE__*/ jsx("span", {
                                        className: "text-heading4",
                                        children: parsePrice(price)
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsxs("span", {
                        className: "text-priceSale-10 text-body3",
                        children: [
                            nights,
                            " ",
                            1 === nights ? 'night' : 'nights',
                            ", ",
                            rooms,
                            " ",
                            1 === rooms ? 'room' : 'rooms'
                        ]
                    })
                ]
            })
        ]
    });
    return /*#__PURE__*/ jsxs(Flex, {
        justifyContent: 'space-between',
        alignItems: 'center',
        width: 1,
        children: [
            /*#__PURE__*/ jsx(Box, {
                width: '65%',
                children: /*#__PURE__*/ jsx(Box, {
                    width: 1,
                    children: resolvedSavingsPct ? /*#__PURE__*/ jsxs(Box, {
                        children: [
                            /*#__PURE__*/ jsxs(Flex, {
                                alignItems: "center",
                                "data-testid": "PENNY_PCLN_VIPICON",
                                children: [
                                    /*#__PURE__*/ jsx(Relative, {
                                        pr: 2,
                                        top: '3px',
                                        children: /*#__PURE__*/ jsx(PricelineLogo, {
                                            width: '60px',
                                            height: '20px'
                                        })
                                    }),
                                    /*#__PURE__*/ jsx(Box, {
                                        children: /*#__PURE__*/ jsx(VipLogo, {
                                            width: '24px',
                                            height: '24px'
                                        })
                                    }),
                                    /*#__PURE__*/ jsx(Relative, {
                                        top: '-3px',
                                        children: /*#__PURE__*/ jsx(Text, {
                                            color: 'pricePrimary.base',
                                            lineHeight: '22px',
                                            ml: 1,
                                            children: /*#__PURE__*/ jsxs(Text.span, {
                                                fontSize: 2,
                                                fontWeight: "bold",
                                                children: [
                                                    parseInt(resolvedSavingsPct, 10),
                                                    "% Off"
                                                ]
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ jsx(Flex, {
                                children: /*#__PURE__*/ jsx(Text, {
                                    fontSize: 0,
                                    color: 'text.light',
                                    children: "Lower price because you're a VIP!"
                                })
                            })
                        ]
                    }) : null
                })
            }),
            /*#__PURE__*/ jsxs(Box, {
                textAlign: 'right',
                children: [
                    parsedStrikePrice > 0 ? /*#__PURE__*/ jsx(Relative, {
                        children: /*#__PURE__*/ jsxs(TextStrikeThrough, {
                            lineHeight: '22px',
                            children: [
                                /*#__PURE__*/ jsx(PriceCurrencyStrikeThrough, {
                                    fontSize: '8px',
                                    fontWeight: "bold",
                                    children: currency
                                }),
                                /*#__PURE__*/ jsx(Text.span, {
                                    fontSize: 1,
                                    fontWeight: "bold",
                                    children: parsedStrikePrice
                                })
                            ]
                        })
                    }) : null,
                    parsedNightlyRate ? /*#__PURE__*/ jsxs(Text, {
                        color: 'pricePrimary.base',
                        lineHeight: '22px',
                        children: [
                            /*#__PURE__*/ jsx(PriceCurrency, {
                                fontSize: 0,
                                fontWeight: "bold",
                                children: currency
                            }),
                            /*#__PURE__*/ jsx(Text.span, {
                                fontSize: 4,
                                fontWeight: "bold",
                                children: parsedNightlyRate
                            })
                        ]
                    }) : null,
                    parsedTotalPrice ? /*#__PURE__*/ jsxs(Text, {
                        fontSize: 0,
                        lineHeight: '16px',
                        color: 'text.light',
                        children: [
                            "Total: ",
                            currency,
                            parsedTotalPrice
                        ]
                    }) : null
                ]
            })
        ]
    });
};
const Price_Price = Price;
export { Price_Price as default };
